import { Injectable } from '@angular/core';
import * as RoleTypPermissionActions from '@carm/_configs/store/actions/role-type-permission.actions';
import * as RoleTypPermissionStore from '@carm/_configs/store/reducers/role-type-permission.reducer';
import * as RoleTypPermissionSelectors from '@carm/_configs/store/selectors/role-type-permissions.selectors';
import { MetadataService } from '@carm/_core/metadata/services';
import { AccessGroup, RoleTypes } from '@carm/_core';
import { IKeyValue } from '@carm/_utils/models';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import {
  Permission,
  RoleTypePermission,
} from '../models/role-type-permissions.model';

@Injectable()
export class RoleTypePermissionService {
  constructor(
    protected store: Store<RoleTypPermissionStore.RoleTypePermissionState>,
    private translateService: TranslateService,
    protected metadataService: MetadataService
  ) {}

  getAllRolePermissions(): Observable<RoleTypePermission[]> {
    return this.store.pipe(
      select(RoleTypPermissionSelectors.getAllRolePermissions)
    );
  }

  isRoleTypePermissionLoaded() {
    return this.store.pipe(
      select(RoleTypPermissionSelectors.isRoleTypePermissionLoaded)
    );
  }

  getPermissions(): Observable<Permission[]> {
    // TODO: in future dispatch load Roles in case roles not found in store
    return this.store.pipe(
      select(RoleTypPermissionSelectors.getProgramPermissions)
    );
  }

  updatePermissions(permissions: Permission[]): void {
    return this.store.dispatch(RoleTypPermissionActions.loadPermissionsSuccess({ data: permissions}));
  }

  clearPermissions(){
    this.store.dispatch(RoleTypPermissionActions.clearPermissions());
  }

  loadAllPermissions(): boolean {
    [
      RoleTypPermissionActions.loadRoleTypePermissions({
        data: RoleTypes.ThirdPartyProgramAccountManager,
      }),
      RoleTypPermissionActions.loadRoleTypePermissions({
        data: RoleTypes.EditRead,
      }),
      RoleTypPermissionActions.loadRoleTypePermissions({
        data: RoleTypes.BusinessAccountManager,
      }),
      RoleTypPermissionActions.loadRoleTypePermissions({
        data: RoleTypes.ProgramAccountManager,
      }),
      RoleTypPermissionActions.loadRoleTypePermissions({
        data: RoleTypes.ReadOnly,
      }),
      RoleTypPermissionActions.loadRoleTypePermissions({
        data: RoleTypes.ThirdPartyBusinessAccountManager,
      }),
    ].forEach(element => {
      this.store.dispatch(element);
    });
    return true;
  }

  loadPermissions(inRoleType: RoleTypes): boolean {
    this.store.dispatch(
      RoleTypPermissionActions.loadRoleTypePermissions({ data: inRoleType })
    );
    return true;
  }

  getAllProgramPermissions(): Observable<Permission[]> {
    return this.store.pipe(
      select(RoleTypPermissionSelectors.getProgramPermissions)
    );
  }

  getAllNativeBusinessPermissions(): Observable<Permission[]> {
    return this.store.pipe(
      select(RoleTypPermissionSelectors.getNativeBusinessPermissions)
    );
  }

  /** this method sort the permission list by resources coming in metadata */
  getPermissionsInResourceOrder(): Observable<Permission[]> {
    return this.getPermissions().pipe(
      take(1),
      switchMap(data => {
        return this.metadataService
          .getResources()
          .pipe(map(resources => ({ roleTypePermission: data, resources })));
      }),
      switchMap(data => {
        if (data.resources && data.resources.length > 0) {
          const output: Permission[] = [];
          data.resources.forEach(resource => {
            const index =
              data.roleTypePermission &&
              data.roleTypePermission.findIndex(
                item => item.resource === resource.key
              );
            if (index !== null && index > -1) {
              output.push({
                resource: resource.key,
                permissionType:
                  data.roleTypePermission[index].permissionType,
              });
            } else {
              output.push({
                resource: resource.key,
                permissionType: null,
              }); // inot exist in the permission list
            }
          });
          return of(output);
        }
        return of([]);
      })
    );
  }

  getAllAccessTypes(): IKeyValue[] {
    return [
      {
        value: this.translateService.instant('concepts.access_group.no_change'),
        key: AccessGroup.NoChange,
      },
      {
        value: this.translateService.instant('concepts.access_group.no_access'),
        key: AccessGroup.NoAccess,
      },
      {
        value: this.translateService.instant('concepts.access_group.read'),
        key: AccessGroup.ReadOnly,
      },
      {
        value: this.translateService.instant('concepts.access_group.edit'),
        key: AccessGroup.EditRead,
      },
      {
        value: this.translateService.instant('concepts.access_group.program_account_manager'),
        key: AccessGroup.ProgramAccountManager,
      },
    ];
  }

  getClientGroupsAccessTypes(): IKeyValue[] {
    return [
      {
        value: this.translateService.instant('concepts.access_group.read'),
        key: AccessGroup.ReadOnly,
      },
      {
        value: this.translateService.instant('concepts.access_group.edit'),
        key: AccessGroup.EditRead,
      },
      {
        value: this.translateService.instant('concepts.access_group.program_account_manager'),
        key: AccessGroup.ProgramAccountManager,
      },
    ];
  }

  getBusinessAccessTypes(): IKeyValue[] {
    return [
      { key: AccessGroup.BusinessManager, value:
        this.translateService.instant('details_relationship.edit_relashionship_access.select_update_access.types.businessManagement') },
      { key: AccessGroup.ProgramManager, value:
        this.translateService.instant('details_relationship.edit_relashionship_access.select_update_access.types.programManagement') },
      { key: AccessGroup.NoAccess, value:
        this.translateService.instant('concepts.access_group.no_access') },
    ];
  }

  getEmployeeAccessRequestTypes(): IKeyValue[] {
    return [
      {
        value: this.translateService.instant('concepts.access_group.no_access'),
        key: AccessGroup.NoAccess,
      },
      {
        value: this.translateService.instant('concepts.access_group.read'),
        key: AccessGroup.ReadOnly,
      },
      {
        value: this.translateService.instant('concepts.access_group.edit'),
        key: AccessGroup.EditRead,
      },
      {
        value: this.translateService.instant('concepts.access_group.program_account_manager'),
        key: AccessGroup.ProgramAccountManager,
      },
    ];
  }
}
